import React from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-accent text-white border border-transparent hover:brightness-105 active:brightness-95",
  secondary: "bg-surface text-ink-secondary border border-edge hover:bg-canvas",
  ghost: "bg-transparent text-ink-secondary border border-transparent hover:bg-hover",
  danger: "bg-danger text-white border border-transparent hover:brightness-105",
};
const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-3.5 text-[13px]",
  md: "h-9 px-4 text-sm",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ variant = "primary", size = "md", className, type = "button", ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          // Full pill — the pre-refresh shape; matches the MUI Button side
          // (theme/lightTheme.ts, darkTheme.ts) so every labeled CTA button
          // reads the same regardless of which Button component rendered it.
          "inline-flex items-center justify-center gap-2 rounded-full font-semibold",
          "transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          VARIANTS[variant], SIZES[size], className,
        )}
        {...rest}
      />
    );
  },
);
